import {
  isExtremeCase,
  mapUpload,
  MediaUpload,
  selectUploadFile,
} from '@wellesley/uploads'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { platformSettingsSlice } from '@wellesley/settings'
import SensitiveContentOverlay from 'components/SensitiveContentOverlay'
import { Flag } from 'lucide-react'

type ChildrenProps = {
  isMediaHidden: boolean
  contentWarning?: string | null
  toggleShowMedia: () => void
}

type ContentWarningWrapperProps = {
  attachments: MediaUpload[]
  summary?: string | null
  containerWidth: number
  children: (props: ChildrenProps) => JSX.Element
}

const ContentWarningWrapper: FC<ContentWarningWrapperProps> = ({
  summary,
  attachments,
  containerWidth,
  children,
}) => {
  const videoExtensions = useSelector(
    platformSettingsSlice.selectors.selectVideoExtensions,
  )

  const [isMediaHidden, setIsMediaHidden] = useState(!!summary)
  const toggleShowMediaRef = useRef<() => void>(() => {
    setIsMediaHidden((prevState) => !prevState)
  })

  useEffect(() => {
    setIsMediaHidden(!!summary)
  }, [summary])

  const hasExtremeSmallMedia = attachments.some((att) => {
    const upload = mapUpload(att)

    const file = selectUploadFile({
      upload,
      allowedExtensions:
        upload?.uploadType === 'VIDEO' ? videoExtensions : undefined,
      size: containerWidth,
    })

    return isExtremeCase(file)
  })

  const toggleShowMedia = toggleShowMediaRef.current
  const canBlurWrapper = isMediaHidden && attachments.length >= 1

  const overlayProps = useMemo(
    () =>
      hasExtremeSmallMedia
        ? { isMediaHidden, blurOnly: true }
        : {
            isMediaHidden,
            toggleShowMedia,
            contentWarning: summary,
          },
    [hasExtremeSmallMedia, isMediaHidden, toggleShowMedia, summary],
  )

  return (
    <>
      {hasExtremeSmallMedia && summary && (
        <div className="mb-3 flex items-center  justify-between font-semibold">
          <div className="flex items-center text-[16px] leading-6 text-gray-700">
            <Flag size={24} className="mr-2" /> Content warning: {summary}
          </div>
          <button
            className="bg-alpha-black-20 h-8 min-w-[60px] cursor-pointer rounded-full px-3 py-1.5 text-sm text-white"
            onClick={toggleShowMedia}
          >
            {isMediaHidden ? 'Show' : 'Hide'}
          </button>
        </div>
      )}
      <div className="relative">
        {canBlurWrapper && <SensitiveContentOverlay {...overlayProps} />}
        {children({
          isMediaHidden,
          contentWarning: summary,
          toggleShowMedia,
        })}
      </div>
    </>
  )
}

export default ContentWarningWrapper
